import React, {ReactNode} from 'react'
import {ObjectInput} from 'react-object-input'
import styled from 'styled-components'
import {ContextMapping, GenericMapper} from '../../../../../common/types'
import {TextEditorWithVariables} from '../../../components/elements/text-editor-with-variables'
import {Button} from '../../../components/library/button'
import {fontXSmall} from '../../../components/library/css'
import {Input} from '../../../components/library/input'
import {Fields} from '../../../components/properties'
import {Section} from '../../../components/properties/section'
import {PropertyEditor} from './property-editor'
import SVGRemove from '../../../../../assets/icons/remove-small.svg'

const EmptyProperty = styled.p`
  ${fontXSmall}
  margin: 0 0 20px;
`
const RemoveWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0px;
`

type ObjectPropertyEditorProps = {
  data: GenericMapper
  onChange: (d: GenericMapper) => void
  pathOptions: string[]
  readOnly?: boolean
  context?: Record<PropertyKey, unknown>
  scriptTheme?: 'light' | 'atomic'
  stepId?: string
  contextMapping?: ContextMapping
}

export const ObjectPropertyEditor = ({
  data,
  onChange,
  pathOptions,
  readOnly,
  context,
  scriptTheme,
  stepId,
  contextMapping
}: ObjectPropertyEditorProps) => {
  // We don't always want to offer the context mapper.
  // for example a request response step is mapping a response body to a variable, not context
  // in that case contextMapping and stepId shouldn't be passed
  const withContextMapper = !!(stepId && contextMapping)

  return (
    <ObjectInput
      obj={data || {}}
      onChange={updater => {
        onChange(updater(data || {}))
      }}
      renderItem={(key, value, updateKey, updateValue, deleteProperty) =>
        !withContextMapper ||
        value?.type === 'script' ||
        value?.type === 'path' ||
        (value && 'path' in value && value.path) ? (
          // ^ paths can be defined without a type
          <PropertyEditor
            name={key}
            getter={value ?? {type: 'path', path: ''}}
            update={updateValue}
            updateName={updateKey}
            pathOptions={pathOptions}
            remove={deleteProperty}
            readOnly={readOnly}
            context={context}
            scriptTheme={scriptTheme}
          />
        ) : (
          <Fields style={{margin: '20px 0'}}>
            <Input
              label="Name"
              value={key}
              onChange={updateKey}
              readOnly={readOnly}
            />
            <TextEditorWithVariables
              label="Value"
              mode="step"
              stepId={stepId}
              contextMapping={contextMapping}
              //@ts-ignore this is safe, we could have {path: ''} value here, but that's safe to convert to {type:static, value: ''}
              value={String(value?.value ?? '')}
              onChange={v => {
                updateValue({
                  type: 'static',
                  value: v
                })
              }}
              readOnly={readOnly}
            />
            <RemoveWrap>
              <Button
                type="button"
                disabled={readOnly}
                appearance="transparent"
                onClick={deleteProperty}
                isRounded={true}
                style={{width: '24px', height: '24px', padding: 0}}
              >
                <SVGRemove />
              </Button>
            </RemoveWrap>
          </Fields>
        )
      }
      renderEmpty={() => <EmptyProperty>No data</EmptyProperty>}
      renderAdd={addItem => (
        <Button
          size="small"
          type="button"
          disabled={readOnly}
          appearance="secondary"
          onClick={addItem}
          style={{width: 'fit-content'}}
        >
          Add item
        </Button>
      )}
    />
  )
}

type WrappedObjectPropertyEditorProps = {
  title: ReactNode
  open?: boolean
  setOpen?: (open: boolean) => void
} & ObjectPropertyEditorProps

export const WrappedObjectPropertyEditor = ({
  open,
  title,
  setOpen,
  ...props
}: WrappedObjectPropertyEditorProps) => {
  const [openState, setOpenState] = React.useState(open)
  return (
    <Section
      readOnly={props.readOnly}
      isOpen={openState}
      setOpen={o => {
        if (setOpen) setOpen(o)
        setOpenState(o)
      }}
      title={title}
    >
      <ObjectPropertyEditor {...props} />
    </Section>
  )
}
